/* src/render/scene.ts — the Three.js scene, renderer and lighting (TASKS.md 1.5).
 *
 * Daylight, not night. The HUD's design tokens are dark and glassy, but the world underneath
 * them is a bright, saturated city — the same split the original uses, and the reason the
 * chrome reads as an overlay rather than as the background.
 */

import {
  AmbientLight,
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  Mesh,
  MeshLambertMaterial,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';

export interface SceneBundle {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  /**
   * Keep the sun and its shadow frustum centred on where the camera is looking. Returns
   * whether the (texel-snapped) frustum actually moved — the caller only needs to re-render
   * the shadow map when it did.
   */
  setSunTarget: (x: number, z: number) => boolean;
  resize: () => void;
  dispose: () => void;
}

/** Daylight sky. Fog is matched to it so the horizon dissolves instead of ending. */
const SKY = 0xa8c8e0;

/*
 * Half-extent of the sun's shadow frustum, metres. This trades shadow *range* against shadow
 * *resolution*: the 2048 map spread over 2×900 m gave 0.88 m per texel, coarse enough that a
 * building's shadow edge came out visibly stair-stepped. 520 m halves that to 0.51 m/texel,
 * which is what makes the soft PCF edge read as soft rather than blocky, and the frustum still
 * covers well beyond the street-level view it follows.
 */
const SHADOW_EXTENT = 520;

export function createScene(canvas: HTMLCanvasElement): SceneBundle {
  const renderer = new WebGLRenderer({
    canvas,
    /*
     * MSAA only below retina density. At DPR ≥ 2 every geometric edge is already sampled
     * twice per CSS pixel, so 4× MSAA is visually near-silent — but it is NOT cost-silent:
     * a city at altitude is millions of one-to-ten-pixel triangles, and per-sample coverage
     * on micro-geometry is exactly where MSAA's cost concentrates. Measured on a retina
     * MacBook at the 1.6 km city overview, this toggle is the difference between ~39 and
     * 60 fps for an edge difference you need a loupe to find.
     */
    antialias: window.devicePixelRatio < 2,
    powerPreference: 'high-performance',
    alpha: false,
    stencil: false,
  });
  renderer.setClearColor(SKY, 1);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;

  const scene = new Scene();
  scene.background = new Color(SKY);
  // Linear fog over a fixed near/far band rather than exponential: it leaves the near city
  // completely untouched and only dissolves the last few hundred metres, where tiles stop.
  scene.fog = new Fog(SKY, 1800, 5200);

  const camera = new PerspectiveCamera(55, 1, 1, 30000);

  /*
   * A bright midday key. These intensities look enormous next to the usual 1.0, and they are
   * meant to: Three's Lambert BRDF divides albedo by π, so the light needed to bring a
   * saturated albedo up to full strength on screen is several times the nominal figure. The
   * numbers below were *fitted*, not guessed — swept against the reference with
   * scripts/tune.mjs, which sets each rig live and samples the resulting pixels, until sunlit
   * grass and warm facades landed on the reference's values (see the sampled targets in
   * geometryWorker's palette comments). Shadowed and vertical surfaces fall to sky+ambient,
   * a little over half the lit value — that ratio is the whole reason the extrusions read as
   * solid rather than flat, and it is why the fill is not raised any further.
   */
  const sun = new DirectionalLight(0xfff4e6, 3.6);
  // ~45° elevation, not overhead. A high sun puts almost all its energy on roofs and ground and
  // leaves the walls to the fill, which is what flattened the facades: at 63° a sunward wall
  // caught only cos(63°)≈0.45 of the key. At 45° it catches ≈0.71, so brickwork reads lit and
  // the extrusions gain the directional contrast the reference has.
  sun.position.set(-300, 380, 240);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -SHADOW_EXTENT;
  sun.shadow.camera.right = SHADOW_EXTENT;
  sun.shadow.camera.top = SHADOW_EXTENT;
  sun.shadow.camera.bottom = -SHADOW_EXTENT;
  sun.shadow.camera.near = 10;
  sun.shadow.camera.far = 2400;
  // Without a bias, flat roofs self-shadow into an acne of dark speckles. normalBias is kept
  // modest: at the tighter frustum a large one visibly detaches shadows from their buildings.
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.25;
  scene.add(sun);
  scene.add(sun.target);

  // Sky/ground hemisphere fill: cool from above, warm bounce from the ground. This is what
  // stops shadowed walls going flat black without lifting the whole scene. The sky tint is only
  // faintly blue on purpose — a strongly blue fill lands on every up-facing surface and drags
  // the grass toward grey-olive, which measurably cost the greens their saturation.
  const sky = new HemisphereLight(0xdfeaf5, 0x8f8256, 1.30);
  scene.add(sky);
  // Ambient sets the shadow floor — how dark a fully-shadowed surface goes. Held down relative
  // to the key: neutral light added everywhere is what washes colour out of a scene, so the
  // shadow floor is paid for mostly by the tinted hemisphere above, not by flat white here.
  scene.add(new AmbientLight(0xffffff, 0.5));

  /*
   * OCEAN under everything. This plane used to be grass; now that the country context layer
   * carries the real Natural Earth coastline as land polygons above it, whatever shows
   * through underneath is, correctly, the sea. The colour is the water layer's vertex value
   * (0.10, 0.30, 0.46 linear) expressed as sRGB, so open ocean and an OSM harbour polygon
   * meet without a visible seam. Sized to out-reach the deepest fog band at full zoom-out.
   */
  const ground = new Mesh(
    new PlaneGeometry(240000, 240000),
    new MeshLambertMaterial({ color: 0x5a93b3 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -8.0;
  // The very first thing painted: the depthless ground-cover layers stack over it by
  // explicit renderOrder (tileMesh.ts), so the sea must already be down when they draw.
  ground.renderOrder = -500;
  ground.receiveShadow = true;
  ground.matrixAutoUpdate = false;
  ground.updateMatrix();
  // Always beneath everything and always in view; skipping the frustum test avoids it being
  // culled by its own bounding sphere at shallow camera angles.
  ground.frustumCulled = false;
  scene.add(ground);

  /*
   * The shadow frustum only spans a kilometre, so it has to follow the camera or shadows stop
   * at a hard line a few blocks out. The sun keeps a fixed *offset* from the target, which
   * keeps the light direction — and therefore every shadow's angle — constant as you pan. The
   * offset must stay identical to the initial sun.position above, or the light silently swings
   * to a different angle on the first frame that moves the camera.
   */
  const SUN_OFFSET = { x: -300, y: 380, z: 240 };
  /*
   * Texel snapping. Sliding the shadow frustum continuously re-rasterises the map from a
   * sub-texel-different position every frame, which makes every shadow edge crawl and boil
   * while the camera pans — the classic directional-shadow shimmer. Snapping the target to
   * whole shadow-map texels in LIGHT space (the frustum's own right/up axes, built exactly
   * the way three's lookAt builds them) means the map re-renders with the same pixels in the
   * same places until the frustum genuinely moves a texel — shadows stand still while you
   * move, and the caller can skip the shadow pass entirely on frames where nothing moved.
   * The light's direction never changes (fixed offset), so shading is untouched.
   */
  const lightZ = new Vector3(SUN_OFFSET.x, SUN_OFFSET.y, SUN_OFFSET.z).normalize();
  const lightX = new Vector3().crossVectors(new Vector3(0, 1, 0), lightZ).normalize();
  const lightY = new Vector3().crossVectors(lightZ, lightX);
  const TEXEL = (2 * SHADOW_EXTENT) / 2048;
  const snapped = new Vector3();
  let lastSnapA = Infinity;
  let lastSnapB = Infinity;
  const setSunTarget = (x: number, z: number): boolean => {
    snapped.set(x, 0, z);
    const rawA = snapped.dot(lightX);
    const rawB = snapped.dot(lightY);
    const a = Math.round(rawA / TEXEL) * TEXEL;
    const b = Math.round(rawB / TEXEL) * TEXEL;
    if (a === lastSnapA && b === lastSnapB) return false;
    lastSnapA = a;
    lastSnapB = b;
    snapped.addScaledVector(lightX, a - rawA).addScaledVector(lightY, b - rawB);
    sun.target.position.copy(snapped);
    sun.target.updateMatrixWorld();
    sun.position.set(
      snapped.x + SUN_OFFSET.x,
      snapped.y + SUN_OFFSET.y,
      snapped.z + SUN_OFFSET.z,
    );
    return true;
  };

  const resize = (): void => {
    const width = canvas.clientWidth || window.innerWidth;
    const height = canvas.clientHeight || window.innerHeight;
    // Cap DPR at 2: beyond that the pixel count grows faster than the visual gain, and this
    // is the single cheapest lever on fill rate for a retina display.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height, false);
    camera.aspect = width / Math.max(1, height);
    camera.updateProjectionMatrix();
  };
  resize();
  window.addEventListener('resize', resize);

  const dispose = (): void => {
    window.removeEventListener('resize', resize);
    ground.geometry.dispose();
    (ground.material as MeshLambertMaterial).dispose();
    renderer.dispose();
  };

  return { renderer, scene, camera, setSunTarget, resize, dispose };
}
